import { useRef, useState, useEffect, ChangeEvent, FunctionComponent } from 'react';

// Libs
import { uploadHttp } from '@Libs/http';
import uploadApi from '@Libs/api/upload';

// Components
import Upload from '@Components/Upload';
import { useFormikContext } from 'formik';
import ButtonLoading from '@Components/UI/ButtonLoading';
import Field, { FieldArea, FieldPhoneNumber } from '../Field';

// Utilities
import classnames from 'classnames';

// Styles
import styles from './FormSupport.module.scss';
import Image from '@Components/Utils/Image';

interface FormProps {
    loading?: boolean;
    className?: string;
}

type Attachment = { file?: any; id?: number; image?: string };

const FormSupport: FunctionComponent<FormProps> = ({ loading, className }) => {
    const uploadRef = useRef(null);
    const { handleSubmit, values, isValid, resetForm, setFieldValue } = useFormikContext();
    const [attachments, setAttachments] = useState<Attachment>({});

    useEffect(() => {
        resetForm();
    }, []);

    const ChooseImage = async (event: ChangeEvent<HTMLInputElement>) => {
        const files: any = Array.from(event.target.files);

        const attachments_upload: Array<Attachment> = await Promise.all(
            files.map(async (file: any, index) => {
                const result = await uploadApi(uploadHttp).generateTemporaryId();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.addEventListener('load', (ev) => {
                        resolve({
                            file,
                            type: file.type,
                            size: file.size,
                            filename: file.name,
                            image: ev.target.result,
                            id: result?.ids?.[0] || Object.keys(attachments).length + index,
                        });
                    });
                    reader.addEventListener('error', () => {
                        reject(new Error('Upload images fail!'));
                    });
                    reader.readAsDataURL(file);
                });
            }),
        );

        const newObjAttachments = attachments;

        attachments_upload.forEach((attachment) => {
            newObjAttachments[attachment.id] = attachment;
        });

        setAttachments(newObjAttachments);
        setFieldValue('attachments', [...values['attachments'], ...attachments_upload]);

        uploadRef.current.reset();
    };

    const removeAttachment = (id: string) => {
        const currentAttachments = { ...attachments };
        delete currentAttachments[id];

        const newAttachments = Object.values(currentAttachments).map((attachment) => attachment.file);

        setAttachments(currentAttachments);
        setFieldValue('attachments', newAttachments);
    };

    const renderAttachment = () => {
        return Object.values(attachments).map((attachment) => (
            <span className={styles.attachment_wrapper} key={attachment.id}>
                <Image src={attachment.image} className={styles.attachment} width={55} height={55} />
                <Image onClick={() => removeAttachment(attachment.id)} className={styles.image_remove} src={'/assets/icon_cancer.png'} width={16} height={16} />
            </span>
        ));
    };

    const wrapperClassName = classnames([styles.wrapper, className]);

    return (
        <form onSubmit={handleSubmit} className={wrapperClassName}>
            <Field className={styles.field} name="name" placeholder="Tên người liên hệ" />

            <FieldPhoneNumber className={styles.field} name="phoneNumber" placeholder="Nhập số điện thoại" />

            <Field className={styles.field} name="email" placeholder="Email liên hệ" />

            <div className={styles.feedback_title}>Hãy cho chúng tôi biết thêm một chút!</div>
            <div className={styles.feedback_description}>
                Chia sẻ trải nghiệm của bạn. Điều gì đang diễn ra tốt đẹp,
                <br />
                điều gì khiến bạn không hài lòng,...
            </div>
            <FieldArea className={styles.field} name="feedback" placeholder="Nhập nội dung bạn muốn hỏi..." />

            <div className={styles.attachment_title}>Thêm file đính kèm</div>
            <div className={styles.attachments}>
                <Upload className={styles.upload_wrapper} multiple ref={uploadRef} onChange={ChooseImage}>
                    <span className={styles.upload}>+</span>
                </Upload>

                {renderAttachment()}
            </div>

            <div className={styles.btn_wrapper}>
                <ButtonLoading disabled={!isValid} loading={loading} className={styles.btn_submit} type="submit">
                    Gửi thông tin
                </ButtonLoading>
            </div>
        </form>
    );
};

FormSupport.defaultProps = {
    className: '',
};

export default FormSupport;
